import { useCallback, useMemo, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { MdOutlineLogout, MdOutlineManageAccounts } from 'react-icons/md';

import './index.css';
import WaveLogo from './Logo/Wave';
import { colors } from '../../../colors';
import { useSelector } from 'react-redux';
import { selectIsDev, selectUserId } from '../../../store/slices/user';
import { persistor } from '../../../store/store';
import { useLogoutMutation } from '../../../store/services/api';
import { GiHamburgerMenu } from 'react-icons/gi';
import {
  FaHome,
  FaSearchDollar,
  FaFistRaised,
  FaCalculator,
} from 'react-icons/fa';
import { IoTicketSharp } from 'react-icons/io5';

interface Props {
  hasPositiveProfit?: boolean;
}

const TopBar = (props: Props) => {
  const { hasPositiveProfit } = props;

  const [logout] = useLogoutMutation();
  const location = useLocation();
  const userId = useSelector(selectUserId);
  const isDev = useSelector(selectIsDev);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const backgroundGradientColor = useMemo(() => {
    if (typeof hasPositiveProfit === 'undefined') {
      return colors.black;
    }

    return hasPositiveProfit
      ? 'linear-gradient(to top, rgba(0, 255, 38, 0.284) -70% , black 80%)'
      : 'linear-gradient(to top, rgba(255, 0, 0, 0.284) -70% , black 80%)';
  }, [hasPositiveProfit]);

  const isOverviewActive = useMemo(
    () =>
      !location.pathname.includes('/bot') &&
      !location.pathname.includes('/discover') &&
      !location.pathname.includes('/sub') &&
      !location.pathname.includes('/control') &&
      !location.pathname.includes('/account') &&
      !location.pathname.includes('/profitCalculations'),
    [location.pathname],
  );
  const overviewColor = useMemo(
    () => (isOverviewActive ? 'white' : '#4d4d4d'),
    [isOverviewActive],
  );

  const isDiscoverActive = location.pathname.includes('/discover');
  const discoverColor = useMemo(
    () => (isDiscoverActive ? 'white' : '#4d4d4d'),
    [isDiscoverActive],
  );

  const isBotsActive = location.pathname.includes('/bot');
  const botColor = isBotsActive ? 'white' : '#4d4d4d';

  const isSubscriptionsActive = location.pathname.includes('/sub');
  const isSubscriptionsVisible = true; // At this moment everyone should see this tab, to see subs or create new ones
  const subscriptionColor = isSubscriptionsActive ? 'white' : '#4d4d4d';

  const isControlActive = useMemo(
    () => location.pathname.includes('/control'),
    [location.pathname],
  );
  const controlColor = useMemo(
    () => (isControlActive ? 'white' : '#4d4d4d'),
    [isControlActive],
  );

  const isProfitCalculationsActive = useMemo(
    () => location.pathname.includes('/profitCalculations'),
    [location.pathname],
  );
  const profitCalculationsColor = useMemo(
    () => (isProfitCalculationsActive ? 'white' : '#4d4d4d'),
    [isProfitCalculationsActive],
  );

  const hamburgerMenu = [
    {
      icon: <FaHome color={colors.white} size={28} />,
      label: 'Overview',
      href: '/',
      onclick: () => setIsMenuOpen(false),
    },
    {
      icon: <FaSearchDollar color={colors.white} size={28} />,
      label: 'Discover',
      href: '/discover',
      onclick: () => setIsMenuOpen(false),
    },
    {
      icon: <IoTicketSharp color={colors.white} size={28} />,
      label: 'Subscriptions',
      href: '/sub',
      onclick: () => setIsMenuOpen(false),
    },
    {
      icon: <FaFistRaised color={colors.white} size={28} />,
      label: 'Control',
      href: '/control',
      onclick: () => setIsMenuOpen(false),
    },
    {
      icon: <FaCalculator color={colors.white} size={28} />,
      label: 'Profit Calcs',
      href: '/profitCalculations',
      onclick: () => setIsMenuOpen(false),
    },
    {
      icon: <MdOutlineManageAccounts color={colors.white} size={28} />,
      label: 'Settings',
      href: '/account',
      onclick: () => setIsMenuOpen(false),
    },
    {
      icon: <MdOutlineLogout color={colors.white} size={28} />,
      href: '/',
      label: 'Log Out',
      onClick: () => logout({ userId }),
    },
  ];

  return (
    <div>
      <div
        className="top-bar-container"
        style={{ backgroundImage: backgroundGradientColor }}
      >
        {/** LOGOS */}

        <WaveLogo
          hasPositiveProfit={hasPositiveProfit}
          label={'AutoMagically'}
          first={true}
        />

        <div className="top-bar-second-logo">
          <WaveLogo
            hasPositiveProfit={hasPositiveProfit}
            label={'GRTS'}
            first={false}
          />
        </div>

        {/** Menu */}
        <div className="top-bar-content">
          {/** Menu */}
          <div className="top-bar-content-menu">
            <Link to={'/'}>
              <h3
                style={{ color: overviewColor }}
                className="top-bar-content-menu-item"
              >
                Overview
              </h3>
            </Link>

            <Link to={'/discover'}>
              <h3
                style={{ color: discoverColor }}
                className="top-bar-content-menu-item"
              >
                Discover
              </h3>
            </Link>

            {/* {isDev && (
            <Link to={'/bot'}>
              <h3
                style={{ color: botColor }}
                className="top-bar-content-menu-item"
              >
                Bots
              </h3>
            </Link>
          )} */}
            {isSubscriptionsVisible && (
              <Link to={'/sub'}>
                <h3
                  style={{ color: subscriptionColor }}
                  className="top-bar-content-menu-item"
                >
                  Subscriptions
                </h3>
              </Link>
            )}

            {isDev && (
              <Link to={'/control'}>
                <h3
                  style={{ color: controlColor }}
                  className="top-bar-content-menu-item"
                >
                  Control
                </h3>
              </Link>
            )}
            {isDev && (
              <Link to={'/profitCalculations'}>
                <h3
                  style={{ color: profitCalculationsColor }}
                  className="top-bar-content-menu-item"
                >
                  Profit Calculations
                </h3>
              </Link>
            )}
          </div>

          <div
            className="top-bar-content-menu-profile"
            style={{
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 24,
            }}
          >
            <Link to={'/account'} style={{ paddingTop: 6 }}>
              <MdOutlineManageAccounts color={colors.white} size={32} />
            </Link>
            {/** Log Out */}
            <MdOutlineLogout
              color={colors.white}
              size={32}
              onClick={() => logout({ userId })}
            />
          </div>
        </div>

        <div
          className="top-bar-hamburger"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <GiHamburgerMenu color={colors.white} size={24} />
        </div>
      </div>
      <div
        className="top-bar-container-menu"
        style={{
          maxHeight: isMenuOpen ? 1000 : 0,
          transition: 'max-height 0.5s ease-out',
        }}
      >
        {hamburgerMenu.map((item) => (
          <Link
            to={item.href}
            key={item.label}
            onClick={item.onClick}
            className="top-bar-hamburger-item"
          >
            {item.icon}
            <span
              style={{
                color: colors.white,
                fontSize: 24,
                fontWeight: 'bold',
                marginLeft: 12,
              }}
            >
              {item.label}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default TopBar;
